import { LEVELS } from "../data/levels.js";

export function getLevel(levelIndex) {
  return LEVELS[levelIndex];
}

export function isFinalLevel(levelIndex) {
  return levelIndex >= LEVELS.length - 1;
}

export function speciesLimitReached(levelIndex, placedCount) {
  return placedCount >= LEVELS[levelIndex].maxSpecies;
}

/**
 * Plain-language restatement of what the level actually asks for — the
 * engine's checkBalance() rule (games/ecosystem/engine/ecosystem.js) plus
 * the level's own distinct-species floor — kept in one place so the biome
 * header and the level-complete modal never drift out of sync with each
 * other or with the actual checks. The species count is read from the
 * level rather than written into the sentence: it used to say "at least 2
 * species" at every level, which was the number Sprout needs and nobody
 * else's.
 */
export function describeStabilityGoal(level) {
  const kinds = level.minSpeciesTypes ?? 1;
  // Sprout keeps the generic wording: its gate asks for one species, but
  // checkBalance() never calls a single-tile biome balanced, so "at least
  // 2" is the number that actually decides a Sprout streak. Every later
  // level has a real distinct-species floor, and saying it here is the
  // whole point — a player shouldn't have to hit a disabled button to
  // find out how wide their web has to be.
  return kinds > 1
    ? `Survive ${level.stableGoal} cycles in a row with ${kinds} different species (including a plant) all alive.`
    : `Survive ${level.stableGoal} cycles in a row with at least 2 species (including a plant) all alive.`;
}

/** The level's goal, in one sentence, for the places (biome header,
 * level-complete modal) that show a single goal line.
 *
 * This used to promise the stability streak AND "at least N rewards from
 * puzzles this level". The reward half is gone: it gated levelling up
 * without ever being the thing the player was told to watch, so a
 * perfectly balanced ecosystem held for 40 cycles simply never completed
 * the level. See docs/decisionlog.md. */
export function describeLevelGoal(level) {
  return describeStabilityGoal(level);
}

/**
 * The stability half of leveling up, split out so main.js's
 * checkLevelCompletion can compare against the same threshold without
 * repeating `state.stableCount >= level.stableGoal` in two places that
 * could quietly drift apart.
 */
export function meetsStabilityGoal(state) {
  return state.stableCount >= LEVELS[state.level].stableGoal;
}

/**
 * Single source of truth for whether the sim is currently allowed to run
 * and why not: insufficient category diversity (meetsDiversityGoal) is
 * the only gate — see the note on data/levels.js's requiredCategories/
 * minCategoryTypes for why it exists (a plants-only biome is otherwise a
 * permanent, zero-risk win). Returns null when nothing is blocking. Used
 * by both biomePanel.js (to render the hint + disable the buttons) and
 * main.js (to know *why* it just had to auto-pause a run in progress).
 *
 * There used to be a second gate here — the sim capped once the
 * stability streak hit its goal while a separate puzzle-answer goal
 * hadn't caught up. That whole reward requirement is gone; levelling up
 * now waits only on the stability streak.
 */
export function getSimBlock(state) {
  const level = LEVELS[state.level];

  if (!meetsDiversityGoal(level, state.placedSpecies)) {
    return { type: "diversity", message: describeMissingDiversity(level, state.placedSpecies) };
  }
  return null;
}

const CATEGORY_ARTICLE_LABEL = {
  plants: "a Plant",
  herbivores: "a Herbivore",
  predators: "a Predator",
  decomposers: "a Decomposer"
};

/**
 * Whether the *placed* biome is diverse enough to run the simulation at
 * all — see the note on data/levels.js's requiredCategories /
 * minCategoryTypes / minSpeciesTypes for why this exists (a plants-only
 * biome is otherwise a permanent, zero-risk win, and a two-species biome
 * used to carry a player from Sprout all the way to Grove). Three
 * separate floors: the categories a level insists on, how many categories
 * it wants, and how many distinct SPECIES it wants. Checked against
 * what's placed right now, not the inventory — a required Herbivore
 * sitting unplaced doesn't count.
 */
export function meetsDiversityGoal(level, placedSpecies) {
  const present = new Set(placedSpecies.map((s) => s.category));
  const hasRequiredCategories = level.requiredCategories.every((cat) => present.has(cat));
  const hasEnoughCategories = present.size >= level.minCategoryTypes;
  const hasEnoughSpecies = countSpeciesTypes(placedSpecies) >= (level.minSpeciesTypes ?? 1);
  return hasRequiredCategories && hasEnoughCategories && hasEnoughSpecies;
}

/**
 * Distinct SPECIES in the biome, which is not the same as tiles: every
 * correct answer mints its own instance, so "Snow Rabbit" and "Whispering
 * Rabbit" are two cards but one species type (games/ecosystem/systems/
 * speciesFactory.js). Keying on `baseId` is what makes the floor mean
 * "a wider web" instead of "more of the same animal".
 *
 * `uid` is the fallback only so a hand-built or pre-baseId object can
 * never be counted as zero types and hard-block a biome that is actually
 * fine; every instance the factory builds carries a baseId, and
 * persistence round-trips it verbatim.
 */
function countSpeciesTypes(placedSpecies) {
  return new Set(placedSpecies.map((s) => s.baseId ?? s.uid)).size;
}

/**
 * Human-readable reason the simulation is currently blocked, or null if
 * it isn't. Ordered most-specific first: a missing *required* category
 * ("add a Predator") names exactly what to do, so it is reported ahead of
 * the category-count shortfall, which in turn is reported ahead of the
 * distinct-species shortfall — a biome short on categories is necessarily
 * short on species too, and "add a Predator" is better advice than "add
 * another kind of species".
 *
 * The species message says out loud that a second Rabbit won't do, since
 * that is exactly the move a player reaches for first (rewards arrive as
 * individual tiles, so owning two Rabbits looks like two species).
 */
export function describeMissingDiversity(level, placedSpecies) {
  const present = new Set(placedSpecies.map((s) => s.category));

  const missing = level.requiredCategories.find((cat) => !present.has(cat));
  if (missing) {
    return `Add ${CATEGORY_ARTICLE_LABEL[missing]} to run the simulation.`;
  }

  const categoryShortfall = level.minCategoryTypes - present.size;
  if (categoryShortfall > 0) {
    return `Add ${categoryShortfall} more species type${categoryShortfall > 1 ? "s" : ""} to run the simulation (${level.minCategoryTypes} needed).`;
  }

  const speciesShortfall = (level.minSpeciesTypes ?? 1) - countSpeciesTypes(placedSpecies);
  if (speciesShortfall > 0) {
    return `Add ${speciesShortfall} more kind${speciesShortfall > 1 ? "s" : ""} of species to run the simulation (${level.minSpeciesTypes} different species needed — another of one you already have doesn't count).`;
  }

  return null;
}

const CATEGORY_ORDER = ["plants", "herbivores", "predators", "decomposers"];

/**
 * Which species categories an *award* should steer toward for a player at
 * `level` who already OWNS `ownedCategories` (a Set — or any iterable —
 * of category strings across their inventory *and* biome, not just what's
 * placed: a predator sitting unplaced still means they aren't stuck).
 * Returns `[]` once the player owns everything this level's diversity
 * gate (meetsDiversityGoal) can ever require of them — at which point
 * species awards are pure chance again.
 *
 * This is the input to games/ecosystem/systems/speciesFactory.js's pickAwardCategory()
 * bad-luck protection: it names the categories a player can be *blocked*
 * on so awards can be nudged (and, past a threshold, forced) toward them
 * instead of leaving someone grinding riddles with no way forward. See
 * docs/decisionlog.md.
 */
export function neededCategoriesForAward(level, ownedCategories) {
  const owned = ownedCategories instanceof Set ? ownedCategories : new Set(ownedCategories);

  // A missing *required* category is always needed.
  const needed = level.requiredCategories.filter((cat) => !owned.has(cat));

  // If owning all of those still wouldn't reach the distinct-type floor,
  // any not-yet-owned category counts toward closing that gap.
  for (const cat of CATEGORY_ORDER) {
    if (owned.size + needed.length >= level.minCategoryTypes) break;
    if (!owned.has(cat) && !needed.includes(cat)) needed.push(cat);
  }

  return needed;
}
